
import Vue from "vue";
import { Assessment } from "@/interfaces/recruiter/candidates/create_assessment";
import { mapGetters, mapActions } from "vuex";
import { FETCH_AVAILABLE_APPLICANTS } from "@/store/modules/recruiter/constants";

interface AssignedApplicant {
  id: string;
  name: string;
  email: string;
}

interface AvailableApplicant {
  value: string;
  label: string;
  email?: string;
}

export default Vue.extend({
  name: "AssessmentForm",
  props: {
    value: {
      type: Object as () => Assessment,
      required: true
    },
    rules: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isValid: true,
      localAssessment: {
        ...this.value,
        difficulty: this.value.difficulty.toLowerCase()
      },
      loadingApplicants: false,
      searchInput: ""
    };
  },
  computed: {
    ...mapGetters("recruiter", {
      availableApplicants: FETCH_AVAILABLE_APPLICANTS
    }),
    mergedApplicants() {
      const assigned = this.localAssessment.assigned_applicants || [];
      const available = this.availableApplicants || [];

      // Create a map to unique applicants by ID (value)
      const applicantMap = new Map();

      // Add assigned applicants first
      assigned.forEach((a: AssignedApplicant) => {
        applicantMap.set(a.id, {
          value: a.id,
          label: a.name || a.email || "Unknown",
          email: a.email
        });
      });

      // Add available applicants (overwriting if needed, or ignoring duplicates)
      available.forEach((a: AvailableApplicant) => {
        if (!applicantMap.has(a.value)) {
          applicantMap.set(a.value, a);
        }
      });

      return Array.from(applicantMap.values());
    },
    difficultyOptions() {
      return [
        { text: "Easy", value: "easy" },
        { text: "Medium", value: "medium" },
        { text: "Hard", value: "hard" }
      ];
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.localAssessment = {
          ...newVal,
          difficulty: newVal.difficulty.toLowerCase()
        };
      },
      deep: true
    }
  },
  methods: {
    ...mapActions("recruiter", {
      fetchAvailableApplicants: FETCH_AVAILABLE_APPLICANTS
    }),
    updateAssessment() {
      this.$emit("input", { ...this.localAssessment });
      this.onValidationChange();
    },
    onValidationChange() {
      this.$emit("validation-change", this.isValid);
    },
    async loadAvailableApplicants() {
      this.loadingApplicants = true;
      try {
        await this.fetchAvailableApplicants();
      } catch (error) {
        console.error("Error loading available applicants:", error);
        this.$store.commit("ROOT_ERROR", {
          message: "Error loading available applicants",
          type: "error"
        });
      } finally {
        this.loadingApplicants = false;
      }
    },
    getApplicantName(applicantId: string): string {
      const applicant = this.availableApplicants.find(
        (a: { value: string; label: string }) => a.value === applicantId
      );
      return applicant ? applicant.label : applicantId;
    }
  },
  async mounted() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await (this as any).loadAvailableApplicants();
  }
});
